import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import ToggleButton from 'components/Luxkit/ToggleButton'
import styled from 'styled-components'
import { rem } from 'polished'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Heading from 'components/Luxkit/Typography/Heading'
import TextButton from 'components/Luxkit/Button/TextButton'
import PaletteProvider from 'contexts/PaletteProvider'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'components/Luxkit/Modal/Modal'
import ListItem from 'components/Luxkit/List/ListItem'
import ResponsiveImage from 'components/Common/ResponsiveImage/ResponsiveImage'
import LineAngleDownIcon from 'components/Luxkit/Icons/line/LineAngleDownIcon'
import Pane from 'components/Common/Pane'
import ModalContext from 'contexts/ModalContext'
import moment from 'moment'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { getTimeZoneShortString } from '.'

const TabsCard = styled(Pane)`
  margin-top: ${rem(-32)};
  border-radius: ${rem(2)};
  padding: ${rem(12)} 0;
  position: relative;
  z-index: 1;
  border-radius: ${rem(2)};
`

const Tabs = styled(CardCarousel)`
  padding: 0 ${rem(12)};
`

interface Props {
  activeEpisode?: App.MarketingLeTVEpisode
  leTVEpisodes: App.MarketingLeTVEpisode[]
}

function EpisodeTabs({ activeEpisode, leTVEpisodes }: Props) {
  const activeEpisodes = useMemo(() => leTVEpisodes.filter(episode => episode.isLive), [leTVEpisodes])
  const inActiveEpisodes = useMemo(() => leTVEpisodes.filter(episode => !episode.isLive), [leTVEpisodes])
  const showModal = useContext(ModalContext)
  const modalButtonText = activeEpisode ? `Ep ${activeEpisode.episode}: ${activeEpisode.header}` : 'About'

  const displayModal = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    showModal(<Modal
      title="Explore more episodes around the world">
      <ListItem
        startIcon={<ResponsiveImage aspectRatio="1:1" sizes="48px" id="o6uxys94abqpwh4chc4" />}
        key="About"
        title="About"
        to="/tv/season-6"
        onClick={() => showModal(null)}
      />
      {activeEpisodes?.length > 0 && <Heading variant="heading6">
        Out now
      </Heading>}
      {activeEpisodes?.length > 0 && activeEpisodes?.map(episode => <ListItem
        startIcon={<ResponsiveImage aspectRatio="1:1" sizes="48px" id={episode.heroImage.id!} />}
        key={episode.header}
        title={String(`Ep ${episode.episode}: ${episode.header}`)}
        to={`/tv/season-6/episode-${episode.episode}`}
        onClick={() => showModal(null)}
      />)}
      {inActiveEpisodes?.length > 0 && <Heading variant="heading6">
        Coming soon
      </Heading>}
      {inActiveEpisodes?.length > 0 && inActiveEpisodes?.map(episode => <ListItem
        startIcon={<ResponsiveImage aspectRatio="1:1" sizes="48px" id={episode.heroImage.id!} />}
        key={episode.header}
        title={<BodyText variant="medium" lineClamp={2} colour="neutral-four">{episode.header}</BodyText>}
        subtitle={<Caption variant="medium" colour="neutral-four">Airing {moment(episode.startDate).format('D MMM YYYY')} • {moment(episode.startDate).format('dddd h:mma')} {getTimeZoneShortString(new Date(episode.startDate))}</Caption>}
      />,
      )}
    </Modal>)
  }, [activeEpisodes, inActiveEpisodes, showModal])

  return (
    <PaletteProvider type="default">
      <CSSBreakpoint max="mobile">
        <VerticalSpacer gap={12}>
          <PaletteProvider type="inverse">
            <Heading variant="heading6" colour="neutral-one">Explore more episodes around the world</Heading>
          </PaletteProvider>
          <TextButton kind="secondary" variant="ghost" endIcon={<LineAngleDownIcon />} onClick={displayModal}>{modalButtonText}</TextButton>
        </VerticalSpacer>
      </CSSBreakpoint>
      <CSSBreakpoint min="tablet">
        <TabsCard size="sm">
          <Tabs gap={0}>
            <ToggleButton
              key="About"
              isSelected={!activeEpisode}
              title="About"
              to="/tv/season-6"
              density="compact"
            />
            {leTVEpisodes?.map((episode) => <ToggleButton
              key={episode.header}
              isSelected={episode.episode === activeEpisode?.episode}
              title={episode.isLive ? `Ep ${episode.episode}: ${episode.header}` : <b>Coming soon</b>}
              subtitle={episode.isLive ? undefined : `Ep ${episode.episode}: ${episode.header}`}
              to={episode.isLive ? `/tv/season-6/episode-${episode.episode}` : undefined}
              density="compact"
            />)}
          </Tabs>
        </TabsCard>
      </CSSBreakpoint>
    </PaletteProvider>
  )
}

export default EpisodeTabs
